import { NaturalNumber_Schema, WholeNumber_Schema } from "@/utilities/schema";
import { z } from "zod";

// Temporary workaround — "Stav" is not marked by the API, identified by name until fixed.
// Tracked in: https://app.asana.com/1/1205261752347246/project/1205266520331763/task/1213596517762888
export const CONDITION_PARAMETER_NAME = "Stav";

export const EDITION_PARAMETER_ID = -1; // We are hiding it for sports cards on FE (https://github.com/ColectioOrg/app.colectio.com/issues/803)
export const PokemonParam_CardNumber_Id = 483529; // Hardcoded - no better way how to identify it
export const PokemonParam_Rarity_Id = 483532; // Hardcoded - no better way how to identify it
export const SportsCardsParam_Sport_Id = 785340; // Hardcoded - no better way how to identify it
export const SportsCardsParam_Sport_ValueSlug_Hokej = "hokej"; // Hardcoded - no better way how to identify it
export const SportsCardsParam_Sport_ValueSlug_Fotbal = "fotbal"; // Hardcoded - no better way how to identify it
export const SportsCardsParam_Sport_ValueSlug_Basketbal = "basketbal"; // Hardcoded - no better way how to identify it
export const SportsCardsParam_Sport_ValueSlug_Mma = "mma"; // Hardcoded - no better way how to identify it
export const SportsCardsParam_Season_Id = 785342; // Hardcoded - no better way how to identify it
export const SportsCardsParam_Athlete_Id = 785373; // Hardcoded - no better way how to identify it
export const SportsCardsParam_Team_Id = 785341; // Hardcoded - no better way how to identify it

// Shared across categories (same slugs confirmed for both Sports Cards and Pokémon)
export const ProductType_ValueSlug_KusovaKarta = "kusova-karta"; // Hardcoded - no better way how to identify it
export const ProductType_ValueSlug_ZabalenyProdukt = "zabaleny-produkt"; // Hardcoded - no better way how to identify it
export const Condition_ValueSlug_PerfektniMintNearMint = "perfektni-mint-near-mint"; // Hardcoded - no better way how to identify it

export const PokemonParam_Theme_ValueSlug_AscendedHeroes = "me-ascended-heroes"; // Hardcoded - no better way how to identify it
export const PokemonParam_Theme_ValueSlug_30thCelebration = "me-30th-celebration"; // Hardcoded - no better way how to identify it
export const PokemonParam_Rarity_ValueSlug_IllustrationRare = "illustration-rare"; // Hardcoded - no better way how to identify it
export const PokemonParam_Foil_ValueSlug_Holofoil = "holofoil"; // Hardcoded - no better way how to identify it
export const PokemonParam_CardType_ValueSlug_Trener = "trener"; // Hardcoded - no better way how to identify it

// Pokémon foil/printing parameter, matched by name — parameter ids differ between environments, but the "Foil" name is stable.
export const FoilParameterName = "Foil";

export const ParameterValueId_Schema = z.number(); // can be negative!
export type ParameterValueId = z.infer<typeof ParameterValueId_Schema>;

export enum ValueRule {
  CollectiblesOnly, // When selected this value, item can be only collectible-based
  AddBackSidePhoto, // When selected this value, allow user to add back side photo
  AiIdentificationAvailable,
}

export const ItemParameterValue_Schema = z
  .object({
    valueId: ParameterValueId_Schema,
    valueName: z.string(),
    valueExtraRule: WholeNumber_Schema, // In fact, it's a bitfield

    // There is way way more, feel free to define them if needed.
  })
  .transform(({ valueExtraRule, valueId, valueName, ...rest }) => {
    const rules: ValueRule[] = [];
    if (valueExtraRule !== null) {
      if (valueExtraRule & 1) {
        rules.push(ValueRule.CollectiblesOnly);
      } // EXTRA_RULE_COLLECTIBLE_SEARCH
      if (valueExtraRule & 2) {
        rules.push(ValueRule.AddBackSidePhoto);
      } // EXTRA_RULE_SHOW_BACK
      if (valueExtraRule & 4) {
        rules.push(ValueRule.AiIdentificationAvailable);
      } // EXTRA_RULE_USE_AI
    }

    return {
      ...rest,
      id: valueId,
      rules,
      value: valueName,
    };
  });
export type ItemParameterValue = z.infer<typeof ItemParameterValue_Schema>;

export const ParameterElasticId_Schema = z.number(); // can be negative!
export type ParameterElasticId = z.infer<typeof ParameterElasticId_Schema>;

export enum ItemParameterViewType {
  Dropdown = "dropdown",
  Checkboxes = "checkboxes",
  Radios = "radios",
  Slider = "slider",
}

export const ItemParameter_Schema = z
  .object({
    id: ParameterElasticId_Schema,
    name: z.string(),
    viewType: z.enum(ItemParameterViewType).nullable(),
    overridable: z.boolean(),
    allowCustomValues: z.boolean(),
    displayOrder: WholeNumber_Schema,
    values: z.array(ItemParameterValue_Schema),
    required: z.boolean(),
    secondary: z.boolean(),
    conditionParameterId: ParameterElasticId_Schema.nullable(),
    conditionValueId: ParameterValueId_Schema.nullable(),
    // There is way more, we do not use them
  })
  .transform(({ required, secondary, ...rest }) => ({
    ...rest,
    isRequired: required,
    isSecondary: secondary,
  }));

export type ItemParameter = z.infer<typeof ItemParameter_Schema>;

// Used in elastic filter
export const ParameterElastic_Schema = z.object({
  id: ParameterElasticId_Schema,
  name: z.string(),

  valueId: ParameterValueId_Schema,
  value: z.string().nullable(),

  // there are also other properties (valueInt, valueBool, ...), but they are not used
});
export type ParameterElastic = z.infer<typeof ParameterElastic_Schema>;

// This is something between ItemParameter and ItemParameterValue - maybe some refactoring in the future?
export const FilledInParameterId_Schema = NaturalNumber_Schema.nullable(); // No idea why it's nullable, just take it as is
export enum FilledInParameterSource {
  Collectible = "collectible",
  Overridden = "overridden",
  Item = "item",
}
export const FilledInParameter_Schema = z
  .object({
    id: FilledInParameterId_Schema,
    keyId: ParameterElasticId_Schema,
    valueName: z.string(),
    allowCustomValues: z.boolean(),
    valueId: ParameterValueId_Schema.nullable(),
    source: z.enum(FilledInParameterSource),

    // There is way more, we do not use them
  })
  .meta({ openapi: "MergedParameter" })
  .transform(({ keyId, valueName, ...rest }) => ({
    ...rest,
    parameterId: keyId,
    value: valueName,
  }));

export type FilledInParameter = z.infer<typeof FilledInParameter_Schema>;
export type FilledInParameterId = z.infer<typeof FilledInParameterId_Schema>;
